import { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

const vertexShader = `
uniform float uTime;
varying vec2 vUv;
varying float vWave;
void main() {
  vUv = uv;
  vec3 p = position;
  float wave = sin(p.x * 1.5 + uTime * 1.25) * 0.30
             + sin(p.x * 2.9 + uTime * 1.9) * 0.13
             + sin(p.y * 2.2 + uTime * 0.9) * 0.07;
  float amp = smoothstep(-3.0, 3.0, p.x);
  p.z += wave * (0.35 + amp * 1.35);
  vWave = clamp(wave, -1.0, 1.0);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}
`;

const fragmentShader = `
precision highp float;
varying vec2 vUv;
varying float vWave;
void main() {
  vec2 uv = vUv;
  vec3 red   = vec3(0.698, 0.133, 0.204);
  vec3 white = vec3(0.949, 0.949, 0.960);
  vec3 blue  = vec3(0.235, 0.231, 0.431);

  float fromTop = floor((1.0 - uv.y) * 13.0);
  vec3 col = mod(fromTop, 2.0) < 0.5 ? red : white;

  float cantonH = 7.0 / 13.0;
  if (uv.x < 0.4 && uv.y > (1.0 - cantonH)) {
    col = blue;
    vec2 lp = vec2(uv.x / 0.4, (uv.y - (1.0 - cantonH)) / cantonH);
    float rows = 9.0;
    float ry = lp.y * rows;
    float row = floor(ry);
    float offset = mod(row, 2.0) * 0.5;
    float cols = 6.0;
    vec2 sp = vec2(fract(lp.x * cols + offset), fract(ry)) - 0.5;
    float d = length(sp);
    float star = smoothstep(0.30, 0.20, d);
    col = mix(col, white, star);
  }

  float shade = 0.80 + 0.20 * vWave;
  col *= shade;
  // soft vignette toward edges
  float vig = smoothstep(1.15, 0.35, distance(uv, vec2(0.5)));
  col *= 0.55 + 0.45 * vig;
  gl_FragColor = vec4(col, 1.0);
}
`;

function FlagMesh() {
  const matRef = useRef();
  const uniforms = useMemo(() => ({ uTime: { value: 0 } }), []);
  useFrame((_, delta) => {
    if (matRef.current) matRef.current.uniforms.uTime.value += delta;
  });
  return (
    <mesh rotation={[-0.12, -0.38, 0.05]} scale={[1.35, 1.35, 1.35]}>
      <planeGeometry args={[10, 6, 120, 72]} />
      <shaderMaterial
        ref={matRef}
        uniforms={uniforms}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

export default function WavingFlag() {
  return (
    <Canvas
      className="!absolute inset-0"
      camera={{ position: [0, 0, 6.2], fov: 42 }}
      gl={{ antialias: true, alpha: true }}
      dpr={[1, 1.8]}
    >
      <FlagMesh />
    </Canvas>
  );
}
